import { useAutoAnimate } from "@formkit/auto-animate/react";
import { faAngleRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useQueryAppointableAsPublic,
  useQueryAppointmentIdAsMember,
  useQueryFacilityAvailabilityCalculateAsMemberNew,
  useQueryHostAvailabilityCalculateAsMemberNew,
} from "@gymflow/api";
import { cn, generateAvailableTimeSlots } from "@gymflow/helpers";
import { usePortalRoutes } from "apps/portal/src/hooks";
import { useClubSettings } from "apps/portal/src/providers";
import { RouteFeature } from "apps/portal/src/routes";
import { DateTime } from "luxon";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import useGymflowModels from "../../../store";
import { Button, Spinner } from "../../atoms";
import { EmptyPage } from "../../atoms/EmptyPage";
import { HostedPagesBackHeader } from "../components/HostedPagesBackHeader";
import useRedirectIfNotAuthenticated from "../useRedirectIfNotAuthenticated";
import HostedPagesAppointableCalendarPicker from "./components/HostedPagesAppointableCalendarPicker";

export type HostedPagesAppointmentSelectTimeProps = { mode: "edit" | "create" };

export const HostedPagesAppointmentSelectTime: React.FC<
  HostedPagesAppointmentSelectTimeProps
> = ({ mode }) => {
  const { appointableId, staffOrFacilityId, appointmentId } = useParams<{
    appointableId?: string;
    staffOrFacilityId: string;
    appointmentId?: string;
  }>();
  const { createSiteOrEmbedLink, isQuickPurchase, isEmbed } = usePortalRoutes();
  useRedirectIfNotAuthenticated(
    createSiteOrEmbedLink(RouteFeature.Appointables),
  );
  const { t } = useTranslation();
  const { api } = useGymflowModels();
  const { club } = useClubSettings();
  const [currentDate, setCurrentDate] = useState(
    DateTime.now().setZone(club.timezone).startOf("day"),
  );
  const { data: dataAppointment, isLoading: isLoadingAppointment } =
    useQueryAppointmentIdAsMember({
      api,
      id: Number(appointmentId),
      enabled: mode === "edit",
    });

  const { data: appointable, isLoading: isLoadingAppointable } =
    useQueryAppointableAsPublic({
      api,
      id:
        mode === "create" && appointableId
          ? parseInt(appointableId)
          : dataAppointment?.appointable.id,
    });
  const isLoadingAppointmentAndAppointable =
    isLoadingAppointment || isLoadingAppointable;
  const hostQuery = useQueryHostAvailabilityCalculateAsMemberNew({
    api,
    opts: {
      appointableId: appointableId
        ? parseInt(appointableId)
        : Number(dataAppointment?.appointable.id),
      hostsIdList:
        staffOrFacilityId === "no-preference" ? undefined : [staffOrFacilityId],
      dateFrom:
        DateTime.max(currentDate, DateTime.now().startOf("minute"))
          .toUTC()
          .toISO({ suppressMilliseconds: true }) ?? "",
      dateTo:
        currentDate
          .plus({
            days: 1,
            seconds: -1,
            minutes:
              (appointable?.duration ?? 0) + (appointable?.blockedTime ?? 0),
          })
          .toUTC()
          .toISO({ suppressMilliseconds: true }) ?? "",
      appointmentIdToIgnore:
        mode === "edit" && appointmentId ? Number(appointmentId) : undefined,
    },
    enabled:
      ["STAFF", "STAFF_AND_FACILITY"].includes(
        appointable?.availabilityType || "",
      ) && !isLoadingAppointmentAndAppointable,
  });

  const facilityQuery = useQueryFacilityAvailabilityCalculateAsMemberNew({
    api,
    opts: {
      appointableId: appointableId
        ? parseInt(appointableId)
        : Number(dataAppointment?.appointable.id),
      facilityIdList:
        staffOrFacilityId === "no-preference"
          ? undefined
          : [Number(staffOrFacilityId)],
      dateFrom:
        DateTime.max(currentDate, DateTime.now().startOf("minute"))
          .toUTC()
          .toISO({ suppressMilliseconds: true }) ?? "",
      dateTo:
        currentDate
          .plus({
            days: 1,
            seconds: -1,
            minutes:
              (appointable?.duration ?? 0) + (appointable?.blockedTime ?? 0),
          })
          .toUTC()
          .toISO({ suppressMilliseconds: true }) ?? "",
      appointmentIdToIgnore:
        mode === "edit" && appointmentId ? Number(appointmentId) : undefined,
    },
    enabled:
      ["FACILITY", "FACILITY_AND_STAFF"].includes(
        appointable?.availabilityType || "",
      ) && !isLoadingAppointmentAndAppointable,
  });
  const { data: availability, isLoading } = [
    "STAFF",
    "STAFF_AND_FACILITY",
  ].includes(appointable?.availabilityType || "")
    ? hostQuery
    : facilityQuery;

  const slots = useMemo(
    () => generateAvailableTimeSlots(availability, club.timezone, appointable),
    [appointable, availability, club.timezone],
  );
  const [parent] = useAutoAnimate();
  const [innerParent] = useAutoAnimate();

  const nextPageLink = (e: { start: DateTime; hosts: string[] }) =>
    createSiteOrEmbedLink(
      (mode === "create"
        ? RouteFeature.AppointableIdReview.replace(
            ":appointableId",
            String(appointableId),
          )
        : RouteFeature.AppointableIdReviewReschedule.replace(
            ":appointmentId",
            String(appointmentId),
          )
      )
        .replace(":staffOrFacilityId", staffOrFacilityId)
        .replace(":timestamp", e.start.toISO() ?? ""),
    );

  return (
    <div
      className={cn(
        "track-height flex w-full flex-col overflow-y-auto items-center bg-gray-50 dark:bg-gray-800",
        {
          "track-height": !isQuickPurchase,
          "h-fit": isEmbed,
          "h-full": !isEmbed,
        },
      )}
    >
      <HostedPagesBackHeader
        label={t("pages.hostedPagesAppointableSelectTime.header")}
      />
      <HostedPagesAppointableCalendarPicker
        currentDate={currentDate}
        setCurrentDate={setCurrentDate}
      />
      <div
        ref={parent}
        className="my-4 flex w-[min(32rem,100vw)] flex-col items-center overflow-y-auto overflow-x-hidden"
      >
        {isLoading && <Spinner />}
        {!isLoading && availability && slots && (
          <div
            ref={innerParent}
            className="flex max-h-full w-[min(32rem,100vw)] flex-col items-center gap-2 px-6"
          >
            {slots.morning.length === 0 && slots.afternoon.length === 0 && (
              <EmptyPage
                textOverride={t(
                  "pages.hostedPagesAppointableSelectTime.noAvailability",
                )}
              />
            )}
            {slots.morning.length > 0 && (
              <div className="w-full text-xs font-semibold uppercase text-gray-500">
                {t("portal.common.morning")}
              </div>
            )}

            {slots.morning.map((e) => {
              return (
                <Button
                  key={e.start.toISO()}
                  className="shadow-xs flex h-fit w-full flex-row items-center justify-between border-gray-200 px-3 py-2 dark:border-gray-800"
                  link={nextPageLink(e)}
                >
                  <div className="dark:text-gray-0 items-start text-sm font-semibold text-gray-950">
                    {e.start
                      .setZone(club.timezone)
                      .toLocaleString(DateTime.TIME_SIMPLE)}
                  </div>
                  <FontAwesomeIcon
                    icon={faAngleRight}
                    className="size-4 text-gray-500"
                  />
                </Button>
              );
            })}
            {slots.afternoon.length > 0 && (
              <div className="w-full pt-4 text-xs font-semibold uppercase text-gray-500">
                {t("portal.common.afternoon")}
              </div>
            )}
            {slots.afternoon.map((e) => {
              return (
                <Button
                  key={e.start.toISO()}
                  className="shadow-xs flex h-fit w-full flex-row items-center justify-between border-gray-200 px-3 py-2 dark:border-gray-800"
                  link={nextPageLink(e)}
                >
                  <div className="dark:text-gray-0 items-start text-sm font-semibold text-gray-950">
                    {e.start
                      .setZone(club.timezone)
                      .toLocaleString(DateTime.TIME_SIMPLE)}
                  </div>
                  <FontAwesomeIcon
                    icon={faAngleRight}
                    className="size-4 text-gray-500"
                  />
                </Button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
